import React from 'react';
import { Check, X } from 'lucide-react';
import type { UserCustomerAccessWithDetails, SectionName } from '../../types/permissions';

interface PermissionMatrixProps {
  access: UserCustomerAccessWithDetails;
  onPermissionChange?: (
    accessId: string,
    section: SectionName,
    key: 'can_view' | 'can_edit',
    value: boolean
  ) => void;
  readOnly?: boolean;
}

interface PermissionMatrixProps {
  access: UserCustomerAccessWithDetails;
  onPermissionChange?: (
    accessId: string,
    section: SectionName,
    key: 'can_view' | 'can_edit',
    value: boolean
  ) => void;
  readOnly?: boolean;
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  access,
  onPermissionChange,
  readOnly = false,
}) => {
  const ALL_SECTIONS: SectionName[] = ['contacts', 'products', 'devices', 'credentials', 'billing'];
  
  const SECTION_DISPLAY: Record<SectionName, { label: string; icon: string }> = {
    contacts: { label: 'Contacts', icon: 'Users' },
    products: { label: 'Products', icon: 'Package' },
    devices: { label: 'Devices', icon: 'Monitor' },
    credentials: { label: 'Credentials', icon: 'Key' },
    billing: { label: 'Billing', icon: 'DollarSign' },
  };

  const getSectionPermissions = (section: SectionName) => {
    return (
      access.section_permissions.find((p) => p.section_name === section) || {
        can_view: true,
        can_edit: false,
      }
    );
  };

  const handleToggle = (
    section: SectionName,
    key: 'can_view' | 'can_edit',
    value: boolean
  ) => {
    if (onPermissionChange && !readOnly) {
      onPermissionChange(access.id, section, key, value);
    }
  };

  return (
    <div className="overflow-x-auto -mx-6">
      <table className="w-full min-w-[600px]">
        <thead className="bg-slate-100 border-y border-slate-200">
          <tr>
            <th className="p-4 text-left text-xs font-bold text-slate-600 uppercase tracking-widest">
              Section
            </th>
            <th className="p-4 text-center text-xs font-bold text-slate-600 uppercase tracking-widest w-24">
              View
            </th>
            <th className="p-4 text-center text-xs font-bold text-slate-600 uppercase tracking-widest w-24">
              Edit
            </th>
          </tr>
        </thead>
        <tbody>
          {ALL_SECTIONS.map((section) => {
            const perms = getSectionPermissions(section);
            const display = SECTION_DISPLAY[section];

            return (
              <tr key={section} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-4">
                  <span className="font-medium text-slate-900">
                    {display.label}
                  </span>
                </td>
                <td className="p-4 text-center">
                  {readOnly ? (
                    perms.can_view ? (
                      <Check className="w-5 h-5 text-green-600 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-slate-300 mx-auto" />
                    )
                  ) : (
                    <input
                      type="checkbox"
                      checked={perms.can_view}
                      onChange={(e) =>
                        handleToggle(section, 'can_view', e.target.checked)
                      }
                      className="w-4 h-4 rounded cursor-pointer accent-accent"
                    />
                  )}
                </td>
                <td className="p-4 text-center">
                  {readOnly ? (
                    perms.can_edit ? (
                      <Check className="w-5 h-5 text-green-600 mx-auto" />
                    ) : (
                      <X className="w-5 h-5 text-slate-300 mx-auto" />
                    )
                  ) : (
                    <input
                      type="checkbox"
                      checked={perms.can_edit}
                      onChange={(e) =>
                        handleToggle(section, 'can_edit', e.target.checked)
                      }
                      disabled={!perms.can_view}
                      className="w-4 h-4 rounded cursor-pointer accent-accent disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
